/**
 * Build a schema.org `VideoObject` for AEO / video rich results.
 *
 * Pass the result to the shared `JsonLd` component so it renders inside the
 * initial server HTML. Fields we don't have are omitted entirely — notably
 * `transcript`, which must never be emitted empty or faked. See
 * `packages/utils/src/json-ld.tsx`.
 */
import { secondsToIso8601Duration } from "./transcript";

export type VideoObjectInput = {
  name: string;
  description: string;
  /** Thumbnail URL(s). A single string is normalized to an array. Omitted when empty/absent. */
  thumbnailUrl: string | string[];
  /** ISO-8601 datetime. Omitted when absent or the `"unknown"` sentinel (never emitted invalid). */
  uploadDate?: string;
  /** Runtime in seconds. Omitted from output when absent. */
  durationSeconds?: number;
  /** Media URL (e.g. Wistia `.m3u8`). */
  contentUrl?: string;
  /** Full plaintext transcript — the AEO payload. Omitted when empty/absent. */
  transcript?: string;
};

export function buildVideoObjectSchema(
  input: VideoObjectInput,
): Record<string, unknown> {
  const transcript = input.transcript?.trim();
  const uploadDate = input.uploadDate?.trim();
  const thumbnailUrl = (
    Array.isArray(input.thumbnailUrl)
      ? input.thumbnailUrl
      : [input.thumbnailUrl]
  ).filter((url) => url?.trim());

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: input.name,
    description: input.description,
  };

  // schema.org uploadDate must be a valid ISO-8601 datetime; skip the "unknown"
  // fallback rather than emit an invalid value that fails the Rich Results Test.
  if (uploadDate && uploadDate !== "unknown") schema.uploadDate = uploadDate;
  if (thumbnailUrl.length) schema.thumbnailUrl = thumbnailUrl;
  if (input.durationSeconds != null) {
    schema.duration = secondsToIso8601Duration(input.durationSeconds);
  }
  if (input.contentUrl) schema.contentUrl = input.contentUrl;
  if (transcript) schema.transcript = transcript;

  return schema;
}
